/** Where a cover comes from. Each source has its own rules for the credit that must be shown with it. */
export type CoverSource = "unsplash" | "commons";

/** A cover picture with the credit that has to be shown beside it. */
export interface Cover {
    src: string;
    source: CoverSource;
    /** Who made it. Empty for a public domain picture from Commons whose author is not known. */
    creditName: string;
    creditUrl: string;
    /** The picture's page on its source */
    sourceUrl: string;
    /** The license of a Commons picture ("CC BY-SA 4.0", "Public domain"), and its page when it has one */
    licenseName?: string;
    licenseUrl?: string;
}

interface CoverFields {
    art?: string | null;
    art_credit_name?: string | null;
    art_credit_url?: string | null;
    art_source_url?: string | null;
    art_source?: string | null;
    art_license_name?: string | null;
    art_license_url?: string | null;
}

const UNSPLASH_IMAGE_HOST = "images.unsplash.com";
const COMMONS_IMAGE_HOSTS = ["upload.wikimedia.org", "thumb.wikimedia.org"];

const isHttps = (value: string | null | undefined, hostOk: (host: string) => boolean): value is string => {
    if (!value) return false;
    try {
        const url = new URL(value);
        return url.protocol === "https:" && !url.username && hostOk(url.hostname);
    } catch {
        return false;
    }
};

const anyHost = () => true;
const isUnsplash = (host: string) => host === "unsplash.com" || host.endsWith(".unsplash.com");
const isCreativeCommons = (host: string) => host === "creativecommons.org";
const isCommonsPage = (host: string) => host === "commons.wikimedia.org";

/** Public domain and CC0 need no author or license page; the Creative Commons licenses with attribution do. */
const needsCredit = (license: string) => !/^(public domain|cc0|pd([\s-]|$))/i.test(license.trim());

const unsplashCover = (item: CoverFields): Cover | null => {
    const { art, art_credit_name: name, art_credit_url: creditUrl, art_source_url: sourceUrl } = item;
    if (!isHttps(art, (host) => host === UNSPLASH_IMAGE_HOST) || !name?.trim()) return null;
    if (!isHttps(creditUrl, isUnsplash) || !isHttps(sourceUrl, isUnsplash)) return null;
    return { src: art, source: "unsplash", creditName: name.trim(), creditUrl, sourceUrl };
};

const commonsCover = (item: CoverFields): Cover | null => {
    const { art, art_credit_name: name, art_credit_url: creditUrl, art_source_url: sourceUrl } = item;
    const license = item.art_license_name?.trim() ?? "";
    const licenseUrl = item.art_license_url;
    if (!isHttps(art, (host) => COMMONS_IMAGE_HOSTS.includes(host)) || !license) return null;
    if (!isHttps(sourceUrl, isCommonsPage) || !isHttps(creditUrl, anyHost)) return null;
    // the author and a link to the license are what the Creative Commons licenses with attribution ask for
    if (needsCredit(license) && (!name?.trim() || !isHttps(licenseUrl, isCreativeCommons))) return null;
    const link = isHttps(licenseUrl, isCreativeCommons) ? licenseUrl : undefined;
    return {
        src: art,
        source: "commons",
        creditName: name?.trim() ?? "",
        creditUrl,
        sourceUrl,
        licenseName: license,
        licenseUrl: link,
    };
};

/**
 * The cover of a focus area or a collection, or null. Pictures are hotlinked from their source, which requires a
 * credit to be shown with each: a picture without a complete credit is not shown. Unsplash wants the photographer
 * and Unsplash as links; Wikimedia Commons wants the author, the license and a link to the file page, except that a
 * public domain picture needs no author.
 */
export const coverOf = (item: CoverFields): Cover | null => {
    switch (item.art_source) {
        case undefined:
        case null:
        case "":
        case "unsplash":
            return unsplashCover(item);
        case "commons":
            return commonsCover(item);
        default:
            return null;
    }
};

/** The widths Wikimedia makes thumbnails in; any other width is refused. */
const WIKIMEDIA_STEPS = [250, 330, 500, 960, 1280, 1920];
const THUMB_WIDTH = /^(.*\/)(\d+)px-([^/]+)$/;

/** The standard width to ask for to get at least `width`, never more than the stored thumbnail, or null when the address is not a thumbnail. */
const wikimediaStep = (url: URL, width: number): { width: number; path: string } | null => {
    const m = url.hostname === "thumb.wikimedia.org" ? THUMB_WIDTH.exec(url.pathname) : null;
    if (!m) return null;
    const stored = Number(m[2]);
    const step = Math.min(WIKIMEDIA_STEPS.find((s) => s >= width) ?? stored, stored);
    return { width: step, path: `${m[1]}${step}px-${m[3]}` };
};

/**
 * The picture at a width. Unsplash resizes through the w parameter, so one stored address serves every size.
 * Wikimedia has thumbnails in fixed steps only, so the nearest step up is taken (never larger than the stored one);
 * an original that is not a thumbnail has one size.
 */
export const coverSrc = (src: string, width: number): string => {
    const url = new URL(src);
    const thumb = wikimediaStep(url, width);
    if (thumb) {
        url.pathname = thumb.path;
        return url.toString();
    }
    if (COMMONS_IMAGE_HOSTS.includes(url.hostname)) return url.toString();
    url.searchParams.set("w", String(width));
    return url.toString();
};

/** The `srcset` for a picture, or "" when it has just one size. */
export const coverSrcSet = (src: string, widths: number[]): string => {
    const url = new URL(src);
    if (COMMONS_IMAGE_HOSTS.includes(url.hostname)) {
        const steps = new Map<number, string>();
        for (const w of widths) {
            const thumb = wikimediaStep(url, w);
            if (thumb && !steps.has(thumb.width)) steps.set(thumb.width, coverSrc(src, w));
        }
        return steps.size < 2 ? "" : [...steps].map(([w, address]) => `${address} ${w}w`).join(", ");
    }
    return widths.map((w) => `${coverSrc(src, w)} ${w}w`).join(", ");
};
